import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface DropdownItem {
  /** Unique identifier for the item */
  id: string | number;
  /** Display label */
  label: string;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Callback when item is clicked */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Divider after this item */
  divider?: boolean;
  /** Danger styling (e.g., for delete actions) */
  danger?: boolean;
}

export interface DropdownProps {
  /** Trigger element (button, link, etc.) */
  trigger: React.ReactNode;
  /** Dropdown menu items */
  items: DropdownItem[];
  /** Alignment of dropdown relative to trigger */
  align?: 'left' | 'right';
  /** Custom className for dropdown menu */
  className?: string;
  /** Whether dropdown is disabled */
  disabled?: boolean;
}

export const handleDropdownItemClick = (
  item: DropdownItem,
  setIsOpen: (open: boolean) => void,
  setFocusedIndex: (index: number) => void
): void => {
  if (item.disabled) return;
  item.onClick?.();
  setIsOpen(false);
  setFocusedIndex(-1);
};

/**
 * Dropdown menu component with keyboard navigation
 *
 * Features:
 * - Click outside to close
 * - Escape key to close
 * - Arrow key navigation
 * - Enter/Space to select
 * - Accessible with ARIA attributes
 *
 * @example
 * ```tsx
 * <Dropdown
 *   trigger={<Button>Actions</Button>}
 *   items={[
 *     { id: 1, label: 'Edit', onClick: handleEdit },
 *     { id: 2, label: 'Delete', onClick: handleDelete, danger: true },
 *   ]}
 * />
 * ```
 */
export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  align = 'left',
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get non-disabled items for keyboard navigation
  const navigableItems = items.filter((item) => !item.disabled);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const updatePosition = () => {
      const rect = dropdownRef.current!.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedMenuHeight = Math.min(items.length * 44 + 16, 400);
      const menuWidth = 224; // 14rem (w-56)

      // Determine vertical position
      const openUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

      let top = openUpward ? rect.top - estimatedMenuHeight - 8 : rect.bottom + 8;
      let left = align === 'right' ? rect.right - menuWidth : rect.left;

      // Ensure menu stays within viewport horizontally
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 16;
      }
      if (left < 16) {
        left = 16;
      }

      // Ensure menu stays within viewport vertically
      if (top < 16) {
        top = 16;
      }
      if (top + estimatedMenuHeight > viewportHeight - 16) {
        top = viewportHeight - estimatedMenuHeight - 16;
      }

      setPosition({ top, left, openUpward });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, items.length, align]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the dropdown trigger or menu
      if (
        (dropdownRef.current && dropdownRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }

      setIsOpen(false);
      setFocusedIndex(-1);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;

        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= navigableItems.length ? 0 : next;
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? navigableItems.length - 1 : next;
          });
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < navigableItems.length) {
            const item = navigableItems[focusedIndex];
            if (item.onClick) {
              item.onClick();
              setIsOpen(false);
              setFocusedIndex(-1);
            }
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusedIndex, navigableItems]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  };

  const handleItemClick = (item: DropdownItem) => {
    handleDropdownItemClick(item, setIsOpen, setFocusedIndex);
  };

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      className={cn(
        'fixed z-[9999] w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5',
        'max-h-96 overflow-y-auto',
        className
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      data-testid="dropdown-menu"
    >
          <div className="py-1">
            {items.map((item, index) => {
              const navigableIndex = navigableItems.findIndex(
                (navItem) => navItem.id === item.id
              );
              const isFocused = navigableIndex === focusedIndex;

              return (
                <React.Fragment key={item.id}>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={cn(
                      'w-full text-left px-4 py-3 md:py-2 text-sm flex items-center gap-2 transition-colors',
                      'min-h-[44px] md:min-h-auto touch-manipulation',
                      item.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : item.danger
                        ? 'text-red-600 hover:bg-red-50 active:bg-red-100'
                        : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200',
                      isFocused && !item.disabled && 'bg-gray-100',
                      !item.disabled && 'cursor-pointer'
                    )}
                    onClick={() => handleItemClick(item)}
                    data-testid={`dropdown-item-${item.id}`}
                  >
                    {item.icon && (
                      <span className="flex-shrink-0">{item.icon}</span>
                    )}
                    <span className="flex-1">{item.label}</span>
                  </button>
                  {item.divider && (
                    <div className="my-1 border-t border-gray-200" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
    </div>
  );

  return (
    <div className="relative inline-block" ref={dropdownRef} data-testid="dropdown">
      {/* Trigger */}
      <div onClick={handleToggle} className="cursor-pointer">
        {trigger}
      </div>

      {/* Dropdown Menu - Rendered via Portal */}
      {dropdownMenu && createPortal(dropdownMenu, document.body)}
    </div>
  );
};

Dropdown.displayName = 'Dropdown';
